import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Quote, Star, Plus, X, Share2, Copy, Check, Image as ImageIcon } from "lucide-react";
import { renderStoryCard, downloadBlob } from "@/lib/storyCard";

// Inline WhatsApp icon to avoid extra dependency
const WhatsAppIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.554-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634L5.092 21.1l3.562-.913zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
  </svg>
);

function StarRow({ rating = 5, size = 14 }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="testimonial-rating">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          strokeWidth={1.5}
          className={i <= rating ? "text-amber" : "text-ink/15"}
          style={i <= rating ? { fill: "#d4a574", color: "#d4a574" } : {}}
        />
      ))}
    </div>
  );
}

function buildShareText(t) {
  const meta = [t.role, t.location].filter(Boolean).join(", ");
  return `"${t.quote}"\n\n— ${t.name}${meta ? ", " + meta : ""}\n\nFrom Just be you · by Mehjabin\n${window.location.origin}`;
}

function ShareMenu({ t }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => buildShareText(t), [t]);

  const shareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const shareNative = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "An experience with Mehjabin",
          text,
          url: window.location.origin,
        });
        setOpen(false);
        return;
      } catch (_e) {
        // user cancelled — fall through silently
      }
    } else {
      await copy();
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied — paste it into Instagram or anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const downloadStory = async () => {
    try {
      toast.loading("Drawing the card…", { id: "story-card" });
      const blob = await renderStoryCard(t);
      downloadBlob(blob, `experience-${(t.name || "story").replace(/[^a-z0-9]/gi, "-")}.png`);
      toast.success("Story image saved — share it to Instagram", { id: "story-card" });
      setOpen(false);
    } catch {
      toast.error("Couldn't render the image", { id: "story-card" });
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-full bg-white/70 hover:bg-white border border-lapis/15 text-lapis flex items-center justify-center transition"
        aria-label="Share this experience"
        data-testid="share-btn"
      >
        <Share2 size={14} strokeWidth={1.6} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[40]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 bottom-12 z-50 glass-strong rounded-2xl p-2 min-w-[180px] shadow-xl shadow-lapis/15" data-testid="share-menu">
            <button onClick={shareWhatsApp} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-sage/15 text-left text-sm text-ink" data-testid="share-whatsapp">
              <span className="text-sage-deep"><WhatsAppIcon size={15} /></span>
              WhatsApp
            </button>
            <button onClick={downloadStory} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-amber/15 text-left text-sm text-ink" data-testid="share-story-png">
              <ImageIcon size={14} className="text-amber" />
              Download Story image
            </button>
            <button onClick={shareNative} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-peach/20 text-left text-sm text-ink" data-testid="share-instagram">
              <Share2 size={14} className="text-peach-deep" />
              Instagram / Story
            </button>
            <button onClick={copy} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-lavender/30 text-left text-sm text-ink" data-testid="share-copy">
              {copied ? <Check size={14} className="text-sage-deep" /> : <Copy size={14} className="text-lapis" />}
              {copied ? "Copied" : "Copy text"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const STAR_FILTERS = [
  { key: null, label: "All" },
  { key: 5, label: "5★" },
  { key: 4, label: "4★" },
  { key: 3, label: "3★" },
  { key: 2, label: "2★" },
  { key: 1, label: "1★" },
];

export default function Experiences() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState(null);

  const refresh = () => {
    api.get("/testimonials", { params: { featured: true } }).then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => { refresh(); }, []);

  return (
    <section className="relative py-24 md:py-32 overflow-hidden" data-testid="experiences-section">
      <div className="absolute -top-10 right-10 w-[420px] h-[420px] bg-sage/25 organic-blob blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-[360px] h-[360px] bg-peach/30 organic-blob blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-14 items-end">
          <div className="md:col-span-7">
            <div className="overline mb-5 flex items-center gap-3">
              Experiences
              {items.length > 0 && (
                <span className="text-ink/45 normal-case tracking-normal font-serif italic text-base" data-testid="experiences-counter">
                  · seen by {items.length} {items.length === 1 ? "soul" : "souls"}
                </span>
              )}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight">
              From the people<br />who arrived <em className="text-peach-deep">here</em>.
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-8 text-ink/65 leading-relaxed">
            Small reflections from those who've sat with me, read the books, or
            simply lingered in the library. Shared with permission.
            <div className="mt-5">
              <button onClick={() => setShowForm(true)} className="btn-soft" data-testid="experiences-share-btn">
                <Plus size={14} /> Share your own
              </button>
            </div>
          </div>
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="experiences-filters">
            {STAR_FILTERS.map((f) => {
              const count = f.key === null ? items.length : items.filter((t) => (t.rating || 5) === f.key).length;
              if (f.key !== null && count === 0) return null;
              return (
                <button
                  key={f.label}
                  onClick={() => setFilter(f.key)}
                  data-testid={`experiences-filter-${f.key ?? "all"}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide border transition ${
                    filter === f.key
                      ? "bg-lapis text-canvas border-lapis"
                      : "bg-white/60 border-lapis/20 text-ink/70 hover:border-lapis hover:text-lapis"
                  }`}
                >
                  {f.label} <span className="opacity-60 ml-1">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-serif italic text-2xl text-ink/45">Soft words are arriving — yours could be among them.</p>
          </div>
        ) : (() => {
          const filtered = filter === null ? items : items.filter((t) => (t.rating || 5) === filter);
          if (filtered.length === 0) {
            return (
              <div className="text-center py-16">
                <p className="font-serif italic text-xl text-ink/45" data-testid="experiences-empty-filtered">
                  No experiences at this rating yet.
                </p>
              </div>
            );
          }
          return (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${filtered.length >= 3 ? "lg:grid-cols-3" : ""} gap-6`}>
            {filtered.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className="glass p-7 rounded-[2rem] relative flex flex-col"
                data-testid={`experience-card-${i}`}
              >
                <div className="absolute top-5 right-5">
                  <ShareMenu t={t} />
                </div>
                <Quote size={26} className="text-peach-deep mb-4" strokeWidth={1.2} />
                <p className="font-serif text-lg italic text-ink/85 leading-relaxed mb-5 flex-1">
                  "{t.quote}"
                </p>
                <StarRow rating={t.rating || 5} />
                <div className="divider-soft my-4" />
                <div className="flex items-center gap-4">
                  {t.avatar ? (
                    <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-lapis/15" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-peach to-lavender flex items-center justify-center text-lapis font-serif text-base">
                      {t.name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-serif text-base text-ink leading-tight">{t.name}</div>
                    <div className="text-xs text-ink/55 mt-0.5">
                      {[t.role, t.location].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          );
        })()}
      </div>

      <SubmitForm open={showForm} onClose={() => setShowForm(false)} onSubmitted={refresh} />
    </section>
  );
}

function SubmitForm({ open, onClose, onSubmitted }) {
  const [form, setForm] = useState({ name: "", location: "", role: "", quote: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 10) {
      toast.error("Just a few more words, please ✨");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/testimonials/submit", form);
      toast.success(data.message || "Thank you — your words are received.");
      setSubmitted(true);
      onSubmitted?.();
    } catch {
      toast.error("Something interrupted that. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setSubmitted(false);
    setForm({ name: "", location: "", role: "", quote: "", rating: 5 });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="experience-submit-modal">
      <div className="glass-strong rounded-[1.75rem] w-full max-w-xl max-h-[92vh] overflow-y-auto p-8 relative">
        <button onClick={close} className="absolute top-5 right-5 text-ink/60 hover:text-lapis" data-testid="experience-submit-close">
          <X size={20} />
        </button>

        {submitted ? (
          <div className="text-center py-12" data-testid="experience-submit-thanks">
            <Quote size={32} className="text-peach-deep mx-auto mb-6" strokeWidth={1.2} />
            <h2 className="font-serif text-3xl text-lapis font-light italic">Thank you, truly.</h2>
            <p className="mt-4 text-ink/65 leading-relaxed max-w-sm mx-auto">
              Your words are received with care. Mehjabin will review them gently before they appear here.
            </p>
            <button onClick={close} className="btn-soft mt-8" data-testid="experience-submit-close-btn">Close</button>
          </div>
        ) : (
          <>
            <div className="overline mb-3">Share your experience</div>
            <h2 className="font-serif text-3xl text-lapis mb-2 font-light">
              A few <em className="text-peach-deep">soft words</em>.
            </h2>
            <p className="text-sm text-ink/60 mb-8">
              Anonymized is welcome — initials, a city, whatever feels right. Mehjabin reviews each one before it goes live.
            </p>

            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-testid="experience-submit-form">
              <div>
                <label className="overline block mb-2">Your name or initials</label>
                <input required className="eth-input" placeholder="e.g. M. or Sara M." value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="experience-submit-name" />
              </div>
              <div>
                <label className="overline block mb-2">Location (optional)</label>
                <input className="eth-input" placeholder="e.g. Bangalore, India" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="experience-submit-location" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">What you experienced (optional)</label>
                <input className="eth-input" placeholder="1:1 mentoring, tarot reading, e-book…" value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="experience-submit-role" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-3">Rating</label>
                <div className="flex items-center gap-1" data-testid="experience-submit-rating">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button key={i} type="button" onClick={() => set("rating", i)} className="p-1 hover:scale-110 transition" data-testid={`experience-submit-star-${i}`} aria-label={`${i} star${i > 1 ? "s" : ""}`}>
                      <Star size={26} strokeWidth={1.4} style={i <= form.rating ? { fill: "#d4a574", color: "#d4a574" } : { color: "rgba(26,37,48,0.2)" }} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Your words</label>
                <textarea required rows={5} className="eth-input resize-none" placeholder="What stayed with you? How did it feel? Whatever wants to be said." value={form.quote} onChange={(e) => set("quote", e.target.value)} data-testid="experience-submit-quote" minLength={10} maxLength={1000} />
                <div className="text-xs text-ink/45 mt-2 flex justify-between">
                  <span>At least 10 characters.</span>
                  <span>{form.quote.length}/1000</span>
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 pt-2">
                <button type="button" onClick={close} className="btn-soft" data-testid="experience-submit-cancel">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="experience-submit-send">
                  {submitting ? "Sending…" : "Share with care"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
